import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Header, Sidebar, SummaryCards, FilterBar, DeleteConfirmModal } from './shared';
import { RecordsTable, RecordModal, ViewRecordModal, ChangeReasonModal, ChangeHistoryModal } from './features/records';
import { VendorListModal, VendorModal, VendorInfoModal } from './features/vendors';
import { DistroListModal, DistroEditModal, DistroViewModal, DistroScheduleModal } from './features/distros';
import { GanttPickerModal, GanttChartModal } from './features/gantt';
import { ReportsListModal, SendReportModal } from './features/reports';
import {
  Record, Vendor, POC, DistroList, DistroEmail, DistroSchedule, DateChangeEntry, ReportRunTarget, Report,
} from './types';
import {
  createRecord, updateRecord, deleteRecord as deleteRecordApi,
  createVendor, updateVendor, deleteVendorApi,
  createPoc, updatePoc, deletePocApi,
  createDistro, updateDistro, deleteDistroApi,
  getReportDownloadUrl, runReports, deleteReportApi, sendReport,
} from './api';
import { showInfoToast, showErrorToast } from './utils/toast';
import { useDataFetching, withErrorHandling } from './hooks/useDataFetching';
import { logger } from './utils/logger';

const DEFAULT_STATUSES = ['In Work', 'In Transit', 'In Planning', 'Scheduled', 'On Hold'];

const AppContent: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const isAuthenticated = Boolean(user?.authenticated);
  const {
    records, vendors, pocs, distros, fleet, reports,
    fetchRecords, fetchVendors, fetchPocs, fetchDistros, fetchFleet, fetchReports,
  } = useDataFetching();

  // ── Filter state ──────────────────────────────────
  const [searchQuery, setSearchQuery] = useState('');
  const [checkType, setCheckType] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(DEFAULT_STATUSES);

  // ── Modal visibility state ────────────────────────
  const [viewRecordOpen, setViewRecordOpen] = useState(false);
  const [viewRecord, setViewRecord] = useState<Record | null>(null);

  const [recordModalOpen, setRecordModalOpen] = useState(false);
  const [editRecord, setEditRecord] = useState<Record | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);

  const [vendorListOpen, setVendorListOpen] = useState(false);
  const [vendorModalOpen, setVendorModalOpen] = useState(false);
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);

  const [vendorInfoOpen, setVendorInfoOpen] = useState(false);
  const [vendorInfoTarget, setVendorInfoTarget] = useState<Vendor | null>(null);
  const [vendorInfoType, setVendorInfoType] = useState<'location' | 'shipping'>('location');

  const [distroListOpen, setDistroListOpen] = useState(false);
  const [distroEditOpen, setDistroEditOpen] = useState(false);
  const [editDistro, setEditDistro] = useState<DistroList | null>(null);

  const [distroViewOpen, setDistroViewOpen] = useState(false);
  const [viewDistro, setViewDistro] = useState<DistroList | null>(null);

  const [distroSchedOpen, setDistroSchedOpen] = useState(false);
  const [schedDistro, setSchedDistro] = useState<DistroList | null>(null);

  const [ganttPickerOpen, setGanttPickerOpen] = useState(false);
  const [ganttChartOpen, setGanttChartOpen] = useState(false);
  const [ganttStart, setGanttStart] = useState('');
  const [ganttEnd, setGanttEnd] = useState('');

  const [reportsOpen, setReportsOpen] = useState(false);
  const [sendReportOpen, setSendReportOpen] = useState(false);
  const [sendReportTarget, setSendReportTarget] = useState<Report | null>(null);

  // ── Change reason/history state ───────────────────
  const [changeReasonOpen, setChangeReasonOpen] = useState(false);
  const [pendingDateChanges, setPendingDateChanges] = useState<{field: string; oldVal: string; newVal: string}[]>([]);
  const [pendingSaveData, setPendingSaveData] = useState<Partial<Record> | null>(null);
  const [changeHistoryOpen, setChangeHistoryOpen] = useState(false);
  const [changeHistoryField, setChangeHistoryField] = useState('');
  const [changeHistoryEntries, setChangeHistoryEntries] = useState<DateChangeEntry[]>([]);

  // ── Initial data load ─────────────────────────────
  // Must wait for auth to finish initializing. The effect runs after the first
  // commit even while the authLoading screen is rendered, so without this guard
  // the fetches fire before the OAuth callback has written the ID token to
  // sessionStorage — every request goes out without an Authorization header and
  // the API Gateway JWT authorizer rejects it with a 401.
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    fetchRecords();
    fetchVendors();
    fetchPocs();
    fetchDistros();
    fetchFleet();
    fetchReports();
  }, [authLoading, isAuthenticated, fetchRecords, fetchVendors, fetchPocs, fetchDistros, fetchFleet, fetchReports]);

  // ── Filtering ─────────────────────────────────────
  const filteredRecords = records.filter((r) => {
    if (checkType && r.check_type !== checkType) return false;
    if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.status)) return false;
    if (selectedStatuses.length === 0) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const searchable = [r.tail, r.aircraft_type, r.check_type, r.status, r.carrier].join(' ').toLowerCase();
      if (!searchable.includes(q)) return false;
    }
    return true;
  }).sort((a, b) => {
    const aDate = a.induction_date || '';
    const bDate = b.induction_date || '';
    if (aDate && bDate) return aDate.localeCompare(bDate);
    if (aDate && !bDate) return -1;
    if (!aDate && bDate) return 1;
    return (a.carrier || '').localeCompare(b.carrier || '');
  });

  // ── Record handlers ───────────────────────────────
  const handleViewRecord = (id: number): void => {
    const r = records.find((rec) => rec.id === id);
    if (r) { setViewRecord(r); setViewRecordOpen(true); }
  };

  const handleEditRecord = (id: number): void => {
    const r = records.find((rec) => rec.id === id);
    if (r) { setEditRecord(r); setIsEditMode(true); setRecordModalOpen(true); }
  };

  const handleNewRecord = (): void => {
    setEditRecord(null); setIsEditMode(false); setRecordModalOpen(true);
  };

  const handleSaveRecord = async (data: Partial<Record>): Promise<void> => {
    try {
      if (isEditMode && editRecord) {
        const changes: {field: string; oldVal: string; newVal: string}[] = [];
        if (data.induction_date !== editRecord.induction_date) {
          changes.push({ field: 'Induction Date', oldVal: editRecord.induction_date || '', newVal: data.induction_date || '' });
        }
        if (data.delivery_date !== editRecord.delivery_date) {
          changes.push({ field: 'Delivery Date', oldVal: editRecord.delivery_date || '', newVal: data.delivery_date || '' });
        }

        if (changes.length > 0) {
          setPendingDateChanges(changes);
          setPendingSaveData(data);
          setChangeReasonOpen(true);
          return;
        }

        const result = await withErrorHandling(() => updateRecord(editRecord.id, data), 'Saving record');
        if (!result) return;
      } else {
        const result = await withErrorHandling(() => createRecord(data), 'Creating record');
        if (!result) return;
      }
      setRecordModalOpen(false);
      await fetchRecords();
    } catch (err) {
      logger.error('Failed to save record', { error: String(err) });
    }
  };

  const handleChangeReasonConfirm = async (reason: string): Promise<void> => {
    if (!pendingSaveData || !editRecord) return;

    try {
      const newHistory: DateChangeEntry[] = [
        ...(editRecord.date_change_history || []),
        ...pendingDateChanges.map((c) => ({
          label: c.field,
          timestamp: new Date().toISOString(),
          old_value: c.oldVal,
          new_value: c.newVal,
          reason,
        })),
      ];

      const dataWithHistory = { ...pendingSaveData, date_change_history: newHistory };
      const result = await withErrorHandling(
        () => updateRecord(editRecord.id, dataWithHistory),
        'Saving record with change history',
      );
      if (!result) return;

      setChangeReasonOpen(false);
      setPendingDateChanges([]);
      setPendingSaveData(null);
      setRecordModalOpen(false);
      await fetchRecords();
    } catch (err) {
      logger.error('Failed to confirm change reason', { error: String(err) });
    }
  };

  const handleDeleteRecord = (id: number): void => {
    setDeleteTargetId(id); setDeleteOpen(true);
  };

  const confirmDelete = async (): Promise<void> => {
    try {
      if (deleteTargetId !== null) {
        await withErrorHandling(() => deleteRecordApi(deleteTargetId), 'Deleting record');
        await fetchRecords();
      }
    } catch (err) {
      logger.error('Failed to delete record', { error: String(err) });
    }
    setDeleteOpen(false); setDeleteTargetId(null);
  };

  // ── Vendor handlers ───────────────────────────────
  const handleOpenVendorList = (): void => { setVendorListOpen(true); };
  const handleAddVendor = (): void => { setEditVendor(null); setVendorModalOpen(true); };
  const handleEditVendor = (vendor: Vendor): void => { setEditVendor(vendor); setVendorModalOpen(true); };

  const handleSaveVendor = async (data: Partial<Vendor>): Promise<void> => {
    try {
      if (editVendor) {
        const result = await withErrorHandling(() => updateVendor(editVendor.id, data), 'Saving vendor');
        if (!result) return;
      } else {
        const result = await withErrorHandling(() => createVendor(data), 'Creating vendor');
        if (!result) return;
      }
      setVendorModalOpen(false);
      await fetchVendors();
    } catch (err) {
      logger.error('Failed to save vendor', { error: String(err) });
    }
  };

  const handleDeleteVendor = async (id: number): Promise<void> => {
    if (!window.confirm('Delete this vendor?')) return;
    try {
      await withErrorHandling(() => deleteVendorApi(id), 'Deleting vendor');
      await fetchVendors();
    } catch (err) {
      logger.error('Failed to delete vendor', { error: String(err) });
    }
  };

  const handleShowVendorInfo = (vendor: Vendor, type: 'location' | 'shipping'): void => {
    setVendorInfoTarget(vendor); setVendorInfoType(type); setVendorInfoOpen(true);
  };

  // ── POC handlers ──────────────────────────────────
  // POCs are managed per vendor from the vendor modal; there is no standalone POC list.
  const handleDeletePoc = async (id: number): Promise<void> => {
    if (!window.confirm('Delete this POC?')) return;
    try {
      await withErrorHandling(() => deletePocApi(id), 'Deleting POC');
      await fetchPocs();
    } catch (err) {
      logger.error('Failed to delete POC', { error: String(err) });
    }
  };

  // Create a POC from within the vendor modal (vendor already pre-filled/locked there).
  const handleAddVendorPoc = async (data: Partial<POC>): Promise<void> => {
    const result = await withErrorHandling(() => createPoc(data), 'Creating POC');
    if (!result) return;
    await fetchPocs();
  };

  // Update a POC edited from within the vendor modal.
  const handleUpdateVendorPoc = async (id: number, data: Partial<POC>): Promise<void> => {
    const result = await withErrorHandling(() => updatePoc(id, data), 'Saving POC');
    if (!result) return;
    await fetchPocs();
  };

  // ── Distro handlers ───────────────────────────────
  const handleOpenDistroList = (): void => { setDistroListOpen(true); };
  const handleCreateDistro = (): void => { setEditDistro(null); setDistroEditOpen(true); };

  const handleViewDistro = (id: number): void => {
    const d = distros.find((dl) => dl.id === id);
    if (d) { setViewDistro(d); setDistroViewOpen(true); }
  };

  const handleEditDistro = (id: number): void => {
    const d = distros.find((dl) => dl.id === id);
    if (d) { setEditDistro(d); setDistroEditOpen(true); }
  };

  const handleSaveDistro = async (data: { name: string; emails: DistroEmail[] }): Promise<void> => {
    try {
      if (editDistro) {
        const result = await withErrorHandling(() => updateDistro(editDistro.id, data), 'Saving distribution list');
        if (!result) return;
      } else {
        const result = await withErrorHandling(() => createDistro(data), 'Creating distribution list');
        if (!result) return;
      }
      setDistroEditOpen(false);
      await fetchDistros();
    } catch (err) {
      logger.error('Failed to save distribution list', { error: String(err) });
    }
  };

  const handleDeleteDistro = async (id: number): Promise<void> => {
    if (!window.confirm('Delete this distribution list?')) return;
    try {
      await withErrorHandling(() => deleteDistroApi(id), 'Deleting distribution list');
      await fetchDistros();
    } catch (err) {
      logger.error('Failed to delete distribution list', { error: String(err) });
    }
  };

  const handleOpenSchedule = (id: number): void => {
    const d = distros.find((dl) => dl.id === id);
    if (d) { setSchedDistro(d); setDistroSchedOpen(true); }
  };

  const handleSaveSchedule = async (schedule: DistroSchedule): Promise<void> => {
    try {
      if (schedDistro) {
        const result = await withErrorHandling(() => updateDistro(schedDistro.id, { schedule }), 'Saving schedule');
        if (!result) return;
      }
      setDistroSchedOpen(false);
      await fetchDistros();
    } catch (err) {
      logger.error('Failed to save schedule', { error: String(err) });
    }
  };

  // ── Change History handler ─────────────────────────
  const handleShowChangeHistory = (record: Record, fieldLabel: string): void => {
    setChangeHistoryEntries(record.date_change_history || []);
    setChangeHistoryField(fieldLabel);
    setChangeHistoryOpen(true);
  };

  // ── Gantt handlers ────────────────────────────────
  const handleOpenGantt = (): void => { setGanttPickerOpen(true); };

  const handleDisplayGantt = (start: string, end: string): void => {
    setGanttStart(start); setGanttEnd(end);
    setGanttPickerOpen(false); setGanttChartOpen(true);
  };

  // ── Reports handlers ──────────────────────────────
  const handleOpenReports = (): void => { setReportsOpen(true); };

  const handleDownloadReport = async (id: string): Promise<void> => {
    try {
      const result = await withErrorHandling(() => getReportDownloadUrl(id), 'Preparing report download');
      if (result?.url) window.open(result.url, '_blank', 'noopener');
    } catch (err) {
      logger.error('Report download failed', { error: String(err) });
    }
  };

  const handleRunReports = async (targets: ReportRunTarget[]): Promise<void> => {
    try {
      const result = await withErrorHandling(() => runReports(targets), 'Running reports');
      if (result) {
        showInfoToast(result.message || 'Reports generated.');
        await fetchReports();
      }
    } catch (err) {
      logger.error('Running reports failed', { error: String(err) });
    }
  };

  const handleSendReport = (id: string): void => {
    const report = reports.find((r) => r.id === id);
    if (!report) { showErrorToast('Report not found.'); return; }
    setSendReportTarget(report);
    setSendReportOpen(true);
  };

  const handleConfirmSendReport = async (to: string[], subject: string): Promise<void> => {
    if (!sendReportTarget) return;
    try {
      const result = await withErrorHandling(() => sendReport(sendReportTarget.id, to, subject), 'Sending report');
      if (result?.sent) {
        showInfoToast(`Report sent to ${result.recipients.length} recipient(s).`);
        setSendReportOpen(false);
      }
    } catch (err) {
      logger.error('Sending report failed', { error: String(err) });
    }
  };

  const handleDeleteReport = async (id: string): Promise<void> => {
    try {
      await withErrorHandling(() => deleteReportApi(id), 'Deleting report');
      await fetchReports();
    } catch (err) {
      logger.error('Deleting report failed', { error: String(err) });
    }
  };

  // ── Render ────────────────────────────────────────
  if (authLoading) {
    return (
      <div className="loading-screen">Loading...</div>
    );
  }

  return (
    <>
      <div className="bg-logo" />
      <div className="bg-logo-right" />
      <div className="dev-credit">Developed by MXO-DEV</div>
      <Header />
      <Sidebar
        onCreateGantt={handleOpenGantt}
        onNewRecord={handleNewRecord}
        onVendor={handleOpenVendorList}
        onDistro={handleOpenDistroList}
        onReports={handleOpenReports}
      />
      <main>
        <SummaryCards records={filteredRecords} />
        <FilterBar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          checkType={checkType}
          onCheckTypeChange={setCheckType}
          selectedStatuses={selectedStatuses}
          onStatusChange={setSelectedStatuses}
        />
        <RecordsTable
          records={filteredRecords}
          onView={handleViewRecord}
          onEdit={handleEditRecord}
          onDelete={handleDeleteRecord}
          onShowChangeHistory={handleShowChangeHistory}
        />
      </main>

      {/* ── Modals ─────────────────────────────────── */}
      <ViewRecordModal active={viewRecordOpen} onClose={() => setViewRecordOpen(false)} record={viewRecord} />
      <RecordModal active={recordModalOpen} onClose={() => setRecordModalOpen(false)} onSave={handleSaveRecord} record={editRecord} vendors={vendors} fleet={fleet} pocs={pocs} isEdit={isEditMode} />
      <DeleteConfirmModal active={deleteOpen} onClose={() => setDeleteOpen(false)} onConfirm={confirmDelete} />
      <VendorListModal active={vendorListOpen} onClose={() => setVendorListOpen(false)} vendors={vendors} onAdd={handleAddVendor} onEdit={handleEditVendor} onDelete={handleDeleteVendor} onShowInfo={handleShowVendorInfo} />
      <VendorModal active={vendorModalOpen} onClose={() => setVendorModalOpen(false)} onSave={handleSaveVendor} vendor={editVendor} pocs={pocs} onSavePoc={handleAddVendorPoc} onUpdatePoc={handleUpdateVendorPoc} onDeletePoc={handleDeletePoc} />
      <VendorInfoModal active={vendorInfoOpen} onClose={() => setVendorInfoOpen(false)} vendor={vendorInfoTarget} infoType={vendorInfoType} />
      <DistroListModal active={distroListOpen} onClose={() => setDistroListOpen(false)} distros={distros} onCreate={handleCreateDistro} onView={handleViewDistro} onEdit={handleEditDistro} onSchedule={handleOpenSchedule} onDelete={handleDeleteDistro} />
      <DistroEditModal active={distroEditOpen} onClose={() => setDistroEditOpen(false)} onSave={handleSaveDistro} distro={editDistro} pocs={pocs} />
      <DistroViewModal active={distroViewOpen} onClose={() => setDistroViewOpen(false)} distro={viewDistro} />
      <DistroScheduleModal active={distroSchedOpen} onClose={() => setDistroSchedOpen(false)} onSave={handleSaveSchedule} distro={schedDistro} />
      <GanttPickerModal active={ganttPickerOpen} onClose={() => setGanttPickerOpen(false)} records={records} onDisplay={handleDisplayGantt} />
      <GanttChartModal active={ganttChartOpen} onClose={() => setGanttChartOpen(false)} records={records} startDate={ganttStart} endDate={ganttEnd} />
      <ReportsListModal active={reportsOpen} onClose={() => setReportsOpen(false)} reports={reports} records={records} vendors={vendors} onRun={handleRunReports} onDownload={handleDownloadReport} onSend={handleSendReport} onDelete={handleDeleteReport} />
      <SendReportModal
        active={sendReportOpen}
        onClose={() => setSendReportOpen(false)}
        report={sendReportTarget}
        distro={sendReportTarget
          ? distros.find((d) => (d.name || '').trim().toLowerCase() === sendReportTarget.entity_name.trim().toLowerCase()) || null
          : null}
        onSend={handleConfirmSendReport}
      />
      <ChangeReasonModal active={changeReasonOpen} onClose={() => { setChangeReasonOpen(false); setPendingDateChanges([]); setPendingSaveData(null); }} onConfirm={handleChangeReasonConfirm} changes={pendingDateChanges} />
      <ChangeHistoryModal active={changeHistoryOpen} onClose={() => setChangeHistoryOpen(false)} fieldLabel={changeHistoryField} history={changeHistoryEntries} />
    </>
  );
};

const App: React.FC = () => (
  <AuthProvider>
    <AppContent />
  </AuthProvider>
);

export default App;
